export const en = {
  title: {
    main: "PROMPT",
    sub: "NINJA",
    remote: "CONTROL",
  },
  common: {
    sync: "Sync",
    refresh: "Refresh",
    save: "Save",
    cancel: "Cancel",
    more: "More options...",
    less: "Less",
    delete: "Delete",
    reconnect: "Reconnect",
    app_name: "PromptNinja",
  },
  menu: {
    home: "Home",
    features: "Features",
    pricing: "Pricing",
    backToHome: "Back to Home",
    validate: "Validate",
    validateLicense: "Validate License",
    start: "Start",
    startHint: "Press PLAY to start",
    startHintShort: "Press PLAY",
  },
  landing: {
    meta: {
      title: "⭐ PromptNinja Free Teleprompter Autocue Online, P2P, Zero Lag Mobile and Laptop",
      description: "The ONLY FREE Autocue Teleprompter designed for zero lag and perfect eye contact. Use your phone as a P2P remote control. Essential for YouTube, Zoom, and professional speeches.",
    },
    hero: {
      headline: "Free Online Autocue. Record looking at the camera, without getting stuck on the text.",
      subheadline:
        "The text scrolls at your pace. Control with your phone or by voice. No apps, no hassle.",
      cta: "🚀 Try for Free Now",
      startHint: "Start Here",
      ctaSecondary: "▶️ See How It Works",
      versionLive: "Version 2.0 Live",
      userCount: "1,247",
      activeCreators: "Active Creators",
      freeTrial: "Free Trial",
      zeroSetup: "Zero Setup. No Install.",
      offlineBadge: "Works Offline",
      privacyBadge: "Total Privacy",
      smallphrase: "No signup • Works right in your browser • Offline after install",
      p2pSection: {
        headline: "Your local network is the tunnel. Zero lag is the rule.",
        subheadline: "Forget the 3-second delay of common apps. With WebRTC P2P technology, what you tap on your phone happens on screen in the same millisecond.",
        badge: "⚡ Latency < 10ms",
      },
      privacySection: {
        headline: "Your data is yours. And yours alone.",
        subheadline: "No accounts, no servers, no cloud. What you type in PromptNinja never leaves your device. Privacy by design, not by promise.",
        badge: "🔒 Zero Tracking",
      },
      pwaSection: {
        headline: "An app that doesn't eat your memory.",
        subheadline: "Install PromptNinja as a native app. Works offline, in full screen, and loads instantly.",
        cta: "📱 Install App on Mobile",
        guideCta: "How to install on iPhone/Android",
      },
    },
    problemSolution: {
      title: 'No more <span class="line-through text-red-500">frustration</span> when recording videos',
      before: {
        title: "Before",
        items: [
          "😩 Forgetting the script midway and having to re-record everything",
          "😤 Wasting hours pausing and adjusting the script manually",
          "🤬 Trying DIY solutions that just don't work properly",
          "🏃 Got up 15 times to pause/continue text",
          "💸 Spending money on apps or gear that lag or complicate things",
          "📱 Downloading apps full of logins, ads, or delays",
        ],
      },
      after: {
        title: "Now",
        items: [
          "🎯 Speak looking straight at the camera, naturally",
          "📱 Control play, pause, and speed from your phone (zero delay)",
          "🗣️ The text follows your voice automatically",
          "🔒 Everything private: your scripts stay only on your device",
          "🌍 Works offline, anywhere (even without internet)",
          "⏱️ Record more videos faster, without re-recording",
        ],
      },
    },
    howItWorks: {
      title: "So simple even your grandma can use it",
      step1: {
        title: "Paste your script",
        desc: "Write or paste your presentation text on your laptop",
      },
      step2: {
        title: "Scan the QR Code",
        desc: "Point your phone camera. Auto connect in 2 seconds",
      },
      step3: {
        title: "Record like a PRO",
        desc: "Control speed, pause, reset. All from your phone",
      },
      cta: "🚀 Start Free",
    },
    comparison: {
      title: "Why PromptNinja?",
      subtitle: "Honest comparison with other solutions",
      columns: {
        ninja: "PromptNinja",
        paid: "Paid Apps",
        hardware: "Hardware",
        free: "Free Sites",
      },
      rows: {
        remote: "Remote Control",
        offline: "Works Offline",
        privacy: "Total Privacy",
        latency: "Latency",
        price: "Upfront Cost",
      },
      values: {
        ninja: {
          remote: "Phone",
          offline: "Yes",
          privacy: "P2P",
          latency: "<10ms",
          price: "Free / $19",
        },
        paid: {
          remote: "No",
          offline: "No",
          privacy: "Cloud",
          latency: "~100ms",
          price: "$ 15/yr",
        },
        hardware: {
          remote: "Physical",
          offline: "Yes",
          privacy: "Yes",
          latency: "<5ms",
          price: "$ 100+",
        },
        free: {
          remote: "No",
          offline: "No",
          privacy: "Trackers",
          latency: ">500ms",
          price: "Free w/ Ads",
        },
      },
    },
    features: {
      title: "Features",
      subtitle: "Everything you need for professional teleprompting",
      badges: {
        free: "FREE",
        pro: "PRO",
      },
      sync: {
        title: "Instant Sync",
        desc:
          "Peer-to-Peer (P2P) technology delivers zero-delay real-time control — no server lag, just instant sync.",
      },
      offline: {
        title: "Offline Mode",
        desc: "Access your teleprompter offline. Your scripts stay private, never leaving your device.",
      },
      privacy: {
        title: "Total Privacy",
        desc: "Ultimate privacy: No cloud, no servers, your script is stored only in your browser.",
      },
      voice: {
        title: "Voice Control (PRO)",
        desc: "Unlock the power of hands-free. Your script scrolls automatically with your voice, keeping you focused.",
        cta: "Unlock PRO Feature 🎤",
      },
      musician: {
        title: "Musician Mode",
        desc: "Scroll the screen with lyrics and chords perfectly aligned. Ideal for musicians who need to follow chords and lyrics during performances.",
        cta: "Learn More about Musician Mode",
        cta2: "Access Musician Mode",
      },
      obs: {
        title: "OBS Studio Integration",
        desc: "Connect directly to OBS via WebSocket. Automate your recordings and switch scenes natively without leaving the teleprompter.",
      },
      themes: {
        title: "Visual Themes",
        desc: "Dark, Light, Matrix, High Contrast, and Chroma Key (green/blue) modes for professional recordings with removable backgrounds.",
      },
      pip: {
        title: "Picture-in-Picture Mode",
        desc: "Keep the teleprompter always visible in a floating window while working in other applications.",
      },
      recording: {
        title: "Audio Recording",
        desc: "Record your script narration and download the audio for review, editing, or content backup.",
      },
      pacing: {
        title: "Pacing Calculator",
        desc: "Calculate your script's estimated reading time (WPM). Adjust text to fit 60 seconds (TikTok/Reels) or 5 minutes (YouTube). Produce optimized content for each platform.",
      },
      shortcuts: {
        title: "Keyboard Shortcuts",
        desc: "Full keyboard control, or use text commands like STOP and PAUSE for automatic control within the script.",
      },
      bilingual: {
        title: "New: Bilingual Mode",
        desc: "View two languages side-by-side. Perfect for language learning or international presentations. Use voice control to track the language of your choice.",
        cta: "Explore Bilingual Mode 🌐",
      },
      focus: {
        title: "Dynamic Focus",
        desc: "Dynamic focus line: Smart highlighting tracks your reading, ensuring you never lose your place.",
      },
      master: {
        title: "Multi-Teleprompter Management",
        desc:
          "Manage multiple teleprompters from a central hub.Perfect for events with multiple presenters.",
      },
    },
    pricing: {
      title: "Simple and Honest Pricing",
      subtitle: "Free trial, upgrade whenever you want",
      free: {
        title: "Free",
        desc: "To test and validate",
        price: "$ 0",
        period: "/forever",
        cta: "Start Free",
        features: ["20 minutes per session", "Unlimited sessions", "Full remote control", "All themes", "OBS Integration (Basic)"],
        missing: ["Unlimited session time", "Voice control", "Custom Shortcuts", "OBS Integration", "Multiple teleprompters management"],
      },
      pro: {
        badge: "🔥 LAUNCH PRICE",
        title: "Lifetime PRO",
        desc: "One-time payment, yours forever",
        price: "$ 19",
        originalPrice: "$ 49",
        priceNote: "Then $ 3/month or $ 49 lifetime",
        cta: "🚀 Get Lifetime Access",
        timer: "⏰ Price valid only for the first 500 users",
        features: [
          "Unlimited sessions (no time limit)",
          "No watermark",
          "Multiple remote controls (teams)",
          "Voice control",
          "Priority support",
          "MIDI support for physical controllers",
          "Early access to features",
          "Customize Keyboard Shortcuts",
          "Native OBS Integration (WebSocket )",
          "Multiple teleprompters management for events",
        ],
      },
      paymentInfo: "💳 One-time payment via Card. \n🔒 7-day guarantee.",
      // Keep old keys for backward compatibility
      lifetime: "Lifetime",
      oneTime: "one-time payment",
      limitedOffer: "Limited Offer",
      conversionNotice: "The final value will be converted to your local currency at checkout.",
    },
    faq: {
      title: "Frequently Asked Questions",
      items: [
        {
          q: "Do I need to install an app?",
          a: "No! It works 100% in the browser. Open on laptop, scan with phone, and that's it. It's a PWA, so you CAN install it if you want.",
        },
        {
          q: "Does it work offline?",
          a: "Yes! The connection is P2P. If both are on the same Wi-Fi or if you've opened the app before, it works offline.",
        },
        {
          q: "Does PromptNinja store my data?",
          a: "No. We use P2P technology to keep your data and scripts locally on your devices. Your privacy is guaranteed.",
        },
        {
          q: "What happens after 20 minutes in the free version?",
          a: "An upgrade invite appears. You can start a new session without daily limits.",
        },
        {
          q: "Which browser does it work on?",
          a: "Chrome, Edge, Safari, and Firefox. We recommend Chrome for best compatibility.",
        },
        {
          q: "What if I don't like it?",
          a: "7-day guarantee. We refund 100% no questions asked.",
        },
      ],
    },
    finalCta: {
      title: "Ready to record like a pro?",
      subtitle: "Join creators who stopped re-recording the same video 15 times",
      ctaPrimary: "Start Free (20 min)",
      ctaSecondary: "See Pricing",
      footer: "No credit card. No setup. No nonsense.",
    },
  },
  status: {
    disconnected: "DISCONNECTED",
    connecting: "CONNECTING...",
    connected: "CONNECTED",
    error: "NETWORK ERROR",
  },
  host: {
    startPrompter: "Presentation Mode",
    editText: "Edit Your Script",
    mirror: "Mirror",
    mirrorV: "Mirror Vertical",
    remoteConnect: "Remote Connect",
    scanInstruction: "Scan the QR Code to Control",
    generatingId: "Generating Link...",
    idLabel: "Session ID:",
    editorPlaceholder: "Paste or type your speech script here to master your delivery...",
    musicEditorPlaceholder: `🎵 Paste your chords or lyrics here...
    
[Am]      [F]
Lyrics and chords
[C]       [G]
Aligned perfectly`,
    localhostWarning: "Warning: Localhost detected. Use the button below to test.",
    offlineWarning: "You are offline. The teleprompter works normally, but the remote control P2P initialization requires internet connection.",
    openRemoteTab: "Open Remote (New Tab)",
    copyLink: "Copy Link",
    linkCopied: "Copied!",
    connectionLost: "CONNECTION LOST",
    watermark: "PROMPT NINJA FREE VERSION",
    exit: "Exit",
    controls: {
      play: "Play",
      pause: "Pause",
      reset: "Reset",
      speed: "Speed",
      margin: "Margin",
      size: "Text",
      timer: "Time",
      caps: "All Caps",
      voice: "Voice Control",
      focusLine: "Focus Line",
      theme: "Visual Theme",
      fontSize: "Font Size",
      fontWeight: "Font Weight",
      isMirrored: "Flip Horizontally",
      display: "Display",
      chroma: "Chroma Key",
      record: "Record",
      voiceLanguage: "Voice Language",
      stopRecord: "Stop Recording",
      downloadRecord: "Download Recording",
      recordNew: "Record New",
      fontConfig: "Font Configuration",
      appearance: "Appearance",
      prevPart: "Previous Part",
      nextPart: "Next Part",
      livePreview: "Live Preview",
      done: "Done",
      livePreviewHelp: "Modify to preview",
    },
    hudLabels: {
      theme: "Theme",
      focus: "Focus",
      voice: "Voice",
      display: "Display",
      chroma: "Chroma Key",
    },
    themes: {
      default: "Default",
      ninja: "Ninja (Dark)",
      paper: "Paper (Light)",
      contrast: "Contrast",
      matrix: "Matrix",
      cyber: "Cyberpunk",
      cream: "Cream (Reader)",
      chroma_green: "Chroma Key (Green)",
      chroma_blue: "Chroma Key (Blue)",
      transparent: "Transparent",
    },
    tips: {
      title: "PRO Editing Tips",
      desc: "Use tags to highlight text:",
      red: "Red Highlight",
      yellow: "Yellow Highlight",
      green: "Green Highlight",
      caps: "Use 'TT' button for All Caps mode.",
      voice: "Enable Mic for voice-activated scrolling.",
    },
    paywall: {
      title: "PRO Feature Locked",
      unlock: "Unlock PRO",
      desc: "This feature is exclusive to Ninja PROs. Unlock to use voice control, keyboard shortcuts, and unlimited time.",
      cta: "Unlock Lifetime",
      trialButton: "Try PRO Mode for 24 hours",
      trialActive: "PRO Mode (Trial) Active",
      close: "Close",
      inputPlaceholder: "Enter PRO key...",
      button: "Activate PRO",
      invalid: "Invalid Key",
      success: "Ninja Pro Activated!",
      emptyKey: "Please enter an unlock key.",
      invalidKey: "Invalid unlock key. Please try again.",
      countdownMessage: "Returning to the page in...",
      deviceLimit: "Device limit reached (12 devices). If you changed devices or formatted your PC, contact support to reset your access.",
      notFound: "Key not found. Check if you typed it correctly or check your Kiwify email (including spam).",
      contactSupport: "Contact Support (WhatsApp)",
      freeAlternativeTitle: "Use FREE and Help Us!",
      leaveReviewCta: "⭐ Keep Using for Free and Leave 5 Stars on Google",
      feedbackQuestion: "Have critics or suggestions?",
      faltam: "Remaining",
      feedbackAction: "Give your feedback",
      trialExpiredTitle: "Ninja for a day!",
      trialExpiredDesc: "We hope your Ninja day was productive! Your settings have been saved, but Pro features are now locked. Get lifetime access to keep evolving.",
    },

    voice: {
      notSupported: "Your browser does not support voice control",
    },
    masterMode: "Multi-Teleprompter",
    masterPanel: "Enter the Control Panel",
    editor: {
      highlight: "Highlight",
      musicianMode: "Musician Mode",
      bilingualMode: "Bilingual Mode",
      bold: "Bold (Ctrl+B)",
      italic: "Italic (Ctrl+I)",
      underline: "Underline (Ctrl+U)",
    },
    autocomplete: {
      title: "Suggested Commands",
      navigate: "Navigate",
      select: "Select",
      results: "results",
      suggestions: {
        speed: { label: "SPEED", desc: "Change scroll speed (Ex: [SPEED 5])" },
        pause: { label: "PAUSE", desc: "Pause for X seconds (Ex: [PAUSE 5])" },
        stop: { label: "STOP", desc: "Stop scrolling (Ex: [STOP])" },
        loop_start: { label: "LOOP START", desc: "Start a repeat section" },
        loop_end: { label: "LOOP END", desc: "End loop with X iterations (Ex: [LOOP 5])" },
        part: { label: "PART", desc: "Mark a script section" },
        bpm: { label: "BPM", desc: "Adjust BPM (Ex: [BPM 120] or [BPM AUTO])" },
        count: { label: "COUNT", desc: "Countdown timer (Ex: [COUNT 10])" },
        rest: { label: "REST", desc: "Rest time (Ex: [REST 15])" },
      }
    },
    defaultText: `Welcome to PromptNinja.

Click on PLAY and read looking at the camera.

The ultimate teleprompter for content creators.

1. Scan the QR Code to connect.
2. Control scroll speed with ease.
3. Use the Trackpad for intuitive navigation.

<r>ATTENTION:</r>
Use color tags to highlight important moments in your script.
<y>This text is in yellow for light emphasis.</y>
<g>And this in green to signal calm or a smooth transition.</g>

Paste your script here and customize your experience to start shining!`,
    ndi: {
      toggleLabel: "NDI Output (OBS/Vmix)",
      toggleDesc: "Broadcast via Screen Capture",
      tutorial: {
        title: "NDI Setup Guide",
        step1: "Open <b>vdo.ninja</b> in a new tab.",
        step2: "Select 'Remote Screenshare'.",
        step3: "In OBS, add 'Browser Source' with the VDO link.",
        step4: "Alternately: Use OBS 'Window Capture' directly.",
        cleanMode: "Clean Mode (No HUD)",
        vingesterConfig: "For native NDI, use Vingester.app",
        openVdo: "Open VDO.Ninja",
        close: "Close",
        cleanModeBtn: "Restart in Clean Mode (?hud=0)",
        masterMode: "Master Mode (Pro)",
        masterPanel: "Master Panel",
      }
    }
  },
  bilingual: {
    mode: "Bilingual Mode",
    primaryLanguage: "Primary Language (Language 1)",
    secondaryLanguage: "Secondary Language (Language 2)",
    primaryPlaceholder: "Paste the primary language text here...",
    secondaryPlaceholder: "Paste the secondary language text here...",
    voiceTrackLabel: "Voice Control Tracks:",
    voiceTrackPrimary: "Primary Language",
    voiceTrackSecondary: "Secondary Language",
    description: "Display two texts side by side for language learning or multilingual presentations.",
    columnInfo: "The two columns will be displayed side by side with a 50/50 ratio.",
    alignmentTip: "Tip: Use [ALIGN]word[/ALIGN] in both texts to sync specific words during scroll.",
    trackLanguage: "Track this language (Voice Control)",
    tracking: "Voice Control active on this tab",
    landscapeTip: "💡 For best mobile experience, rotate to landscape mode (horizontal)",
    languages: {
      pt: "Portuguese",
      en: "English",
      es: "Spanish",
      it: "Italian",
      fr: "French",
      de: "German",
      ja: "Japanese",
      zh: "Chinese",
      other: "Other",
      auto: "Automatic (Site Language)"
    },
  },
  pacing: {
    title: "Reading Time Calculator",
    wordCount: "Words",
    estimatedTime: "Estimated Time",
    wpmLabel: "Words Per Minute (WPM)",
    slow: "Slow (120 WPM)",
    medium: "Medium (150 WPM)",
    fast: "Fast (180 WPM)",
    totalWithPauses: {
      title: "Total Duration",
      details: "Without pauses -",
      pauses: "With pauses -",
    },
    platforms: {
      title: "Platform Recommendations",
      tiktok: "TikTok/Reels (60s)",
      youtube: "YouTube (5 min)",
    },
    suggestions: {
      tooLong: "Your script is long. Consider reducing to ~{{words}} words.",
      tooShort: "Your script is short. Consider adding ~{{words}} words.",
      perfect: "Perfect!",
    },
    custom: {
      title: "Custom WPM",
      label: "Your Speed (WPM)",
      result: "Estimated Time",
    },
    button: "Calculate Time",
  },
  paywall: {
    title: "Customize Keyboard Shortcuts",
    unlock: "Unlock PRO",
  },
  remote: {
    connecting: "Searching Ninja Host...",
    targetId: "Target: {{id}}",
    touchArea: "TRACKPAD",
    speed: "Speed",
    reset: "Reset",
    start: "Start",
    pause: "Pause",
    stop: "Stop",
    theme: "Theme",
    language: "Language",
    bilingualEditTitle: "Editing Locked",
    bilingualEditWarning: "Editing is disabled in bilingual mode. Use the main computer.",
    iosMicGuidance: {
      title: "iOS/Safari Tip",
      description: "To avoid repeated permission prompts, tap the 'AA' icon in the address bar, select 'Website Settings' or '...', and change 'Microphone' to 'Allow'.",
      button: "How to configure",
      step1: "Tap the 'AA' icon in the address bar",
      step2: "Select 'Website Settings' or '...' and change 'Microphone' to 'Allow'",
    },
    voicePermission: {
      title: "Microphone Blocked",
      description: "Safari blocked access. Click below to try re-activating or follow the tip in settings.",
      action: "Try Again",
    },
    proFeature: "Pro Feature",
  },
  share: {
    button: "Share PromptNinja",
    title: "Share PromptNinja!",
    text: "My videos are perfect now! 🎬 I found PromptNinja - FREE online teleprompter, P2P with zero lag. My phone is the remote control!",
    success: "Thanks for sharing! ❤️",
    notSupported: "Sharing not available in this browser",
    cardTitle: "Share",
    cardDescription: "Enjoying PromptNinja? Share with friends and help other creators!",
  },

  tutorial: {
    tips: {
      title: "Pro Tips",
      tip1: "Use voice control for hands-free operation.",
      tip2: "Scan the QR code to control from your phone.",
    },
    title: "PromptNinja: Your Quick Start Guide",
    overview: {
      title: "Overview",
      description:
        "PromptNinja is a peer-to-peer professional teleprompter that transforms your browser into a full studio and your smartphone into a remote control — no apps, no accounts, no server reliance.",
    },
    howToUse: {
      title: "How to Use",
      step1: {
        title: "Write Your Script",
        description:
          "Easily type or paste your presentation script. Use tags like <r>text</r> for color highlighting.",
      },
      step2: {
        title: "Connect Your Phone",
        description:
          "Connect your smartphone: Scan the QR code to turn it into a remote control for speed and scrolling.",
      },
      step3: {
        title: "Presentation Mode",
        description:
          "Activate teleprompter mode: Click 'Start Presentation' then control with your remote or voice.",
      },
    },
    advancedFeatures: {
      title: "Advanced Features",
      voiceControl: {
        title: "Voice Control (Pro)",
        description:
          "Enable voice-activated scrolling: Your teleprompter advances automatically as you speak. (Pro feature)",
      },
      themes: {
        title: "Themes",
        description:
          "Customize the teleprompter's appearance with different themes for better readability in various lighting conditions.",
      },
      focusMode: {
        title: "Focus Mode",
        description: "Activate Focus Mode to highlight the reading line and dim the surrounding text.",
      },
      noControls: {
        title: "No Controls Mode (HUD-less)",
        description:
          "For a cleaner experience, enable No Controls mode. The interface hides, but you maintain full control via mobile remote or keyboard shortcuts.",
      },
      musicianMode: {
        title: "Musician Mode",
        description:
          "Perfect for chords and lyrics. This mode preserves original formatting and prevents unwanted line breaks, ideal for playing and singing along.",
      },
      bilingualMode: {
        title: "Bilingual Mode",
        description:
          "Display two texts side-by-side. Ideal for language learning or multilingual presentations. Includes selectable voice control tracking.",
      },
    },
    commands: {
      title: "Smart Text Commands",
      subtitle: "Automate your script with bracket commands",
      speed: {
        title: "Dynamic Speed",
        desc: "Change scroll speed automatically.",
        example: "[SPEED 5]",
      },
      loop: {
        title: "Loop / Repeat",
        desc: "Create loops for exercises or choruses. Repeats section between START and count.",
        example: "[LOOP START] ... [LOOP 3]",
      },
      pause: {
        title: "Scheduled Pause",
        desc: "Pauses scrolling for X seconds then resumes automatically.",
        example: "[PAUSE 5]",
      },
      stop: {
        title: "Stop",
        desc: "Stops scrolling automatically.",
        example: "[STOP]",
      },
      next: {
        title: "Next or Previous",
        desc: "Advances or goes back to the next part. Use keyboard shortcuts ( →, ←).",
        example: "[PART X] [SLIDE X]",
      },
      count: {
        title: "Count",
        desc: "Count of repetitions. Ideal for exercises (made for integration with voice control).",
        example: "[COUNT 10]",
      },
      rest: {
        title: "Rest",
        desc: "Rest for X seconds then resumes automatically. Ideal for exercises.",
        example: "[REST 5]",
      },
    },
  },
  script: {
    manager: "Scripts",
    yourScripts: "Your Scripts",
    untitled: "Untitled Script",
    new: "New Script",
    rename: "Rename Script",
    deleteConfirm: "Are you sure you want to delete this script?",
    trash: "Trash",
    emptyTrash: "Your trash is empty",
    restore: "Restore",
    permanentlyDelete: "Permanently Delete",
    autoCleanupNote: "Scripts are automatically deleted after 14 days",
  },
  footer: {
    copyright: "PromptNinja. All rights reserved.",
    links: {
      resources: "Resources",
      oratory: "Public Speaking Tips",
      alternatives: "Alternatives",
      whatIs: "What Is Teleprompter",
    },
  },
  editor: {
    findReplace: "Find and Replace",
    find: "Find",
    findPlaceholder: "Type the text to search...",
    replacePlaceholder: "Type the text to replace...",
    replaceWith: "Replace with",
    replace: "Replace",
    findNext: "Find Next",
    replaceAll: "Replace All",
    undo: "Undo",
    matches: "matches",
    cleanText: "Clean Text",
    removeLineBreaks: "Remove Line Breaks",
  },
  thankYou: {
    title: "Thank you for your purchase!",
    subtitle:
      "Your payment has been successfully confirmed. You are now one step away from unlocking the full power of PromptNinja Pro.",
    success: {
      title: "PromptNinja Pro Activated!",
      message: "Your account has been successfully updated. Enjoy all unlimited features.",
      button: "Start Using Now",
    },
    activation: {
      title: "Activate your license",
      description: "Enter the activation code you received in your email (or on the Kiwify confirmation page).",
      label: "Activation Code",
      placeholder: "ENTER YOUR CODE HERE",
      button: "Activate PromptNinja Pro",
      validating: "Validating...",
      skip: "Skip activation and go to Free App",
      error: {
        invalid: "Invalid or already used code.",
        connection: "Connection error. Check your internet and try again.",
      },
    },
    footer: "© {{year}} PromptNinja. All rights reserved.",
  },
  seo: {
    scripts: {
      title: "Teleprompter Ready-to-Use Scripts",
      description: "Complete collection of free scripts for your videos.",
    },
  },
  hudless: {
    button: "Start HUD-less",
    tooltip: "Starts teleprompter without visible controls. Control via mobile or hotkeys.",
    showControls: "Show Controls",
  },
  hotkeys: {
    title: "Keyboard Shortcuts",
    proDescription: "Customizing hotkeys is a PRO feature.",
    reset: "Reset Defaults",
    pressKey: "Press key...",
    actions: {
      TOGGLE_PLAY: "Play / Pause",
      SPEED_UP: "Speed Up",
      SPEED_DOWN: "Speed Down",
      FONT_INCREASE: "Increase Font",
      FONT_DECREASE: "Decrease Font",
      TOGGLE_MIRROR: "Mirror",
      TOGGLE_FLIP: "Flip Vertical",
      TOGGLE_FOCUS: "Focus Mode",
      EXIT: "Exit",
      RESET: "Reset",
      TOGGLE_HUD: "Toggle HUD",
      TOGGLE_CAMERA: "Toggle Camera",
      TOGGLE_WIDGET: "Toggle PiP",
      NEXT_PART: "Next Part",
      PREVIOUS_PART: "Previous Part",
      FORMAT_BOLD: "Format Bold",
      FORMAT_ITALIC: "Format Italic",
      FORMAT_UNDERLINE: "Format Underline",
      FORMAT_RED: "Color: Red",
      FORMAT_YELLOW: "Color: Yellow",
      FORMAT_GREEN: "Color: Green",
      FORMAT_BLUE: "Color: Blue",
    },
  },
  voice_analytics: {
    title: "Presentation Finished",
    congratulations: "Amazing! You finished the script.",
    subtitle: "Here's the summary of your performance in this session:",
    duration: "Duration",
    wpm: "WPM (Speed)",
    accuracy: "Accuracy",
    sentences: "Sentences",
    close: "Close Summary",
  },
  music: {
    bpmSync: "BPM",
    backingTrackUpload: "Add Backing Track",
    bpmTeaser: "Auto BPM",
    hero: {
      badge: "Stage Mode",
      headline: "Teleprompter for Musicians:",
      subHeadline: "Infinite Setlists, Zero Lag & Pedal Control",
      description: "Create setlists with auto-scroll, BPM sync, and P2P mobile control. Perfect for independent bands, no freezing on stage.",
      cta: "Create Free Setlist Now",
      ctaSecondary: "See It in Action",
    },
    features: {
      headline: "Everything for Your Show",
      subheadline: "Essential tools for rehearsals and live performances.",
      setlist: {
        title: "Custom Setlists",
        desc: "Add and reorder songs freely. Edit chords with monospaced font for perfect alignment.",
      },
      offline: {
        title: "Offline PWA",
        desc: "Install on tablet or PC. Works without internet ensuring total stability on stage.",
      },
      themes: {
        title: "Night Mode (Stage Mode)",
        desc: "Dark background and contrasting lyrics. Perfect for low-light environments without blinding you.",
      },
      lyrics: {
        title: "Zero Lag P2P",
        desc: "Remote control via mobile with instant response. Essential stability for long shows.",
      },
      pedal: {
        title: "Pedals & Hotkeys",
        desc: "Native shortcuts for Page Up/Down. Compatible with bluetooth pedals for foot control.",
      },
      bpm: {
        title: "BPM Sync",
        desc: "Auto-scroll to the band's rhythm. Listen to live sound or sync with the metronome.",
      },
      backingSync: {
        title: "Backing Tracks Sync",
        desc: "Markers & 15s Preview. Synchronize text with your guide audio or backing track.",
      },
      voice: {
        desc: "Let PromptNinja turn the page for you. Voice recognition follows your singing.",
      },
      pro: {
        desc: "Unlock customizable MIDI, infinite setlists, and multi-device use.",
      },
    },
    landing: {
      meta: {
        title: "Free Teleprompter for Musicians: Unlimited Setlists, Auto BPM & Backing Sync | PromptNinja Music",
        description: "The best teleprompter for musicians. Create setlists, scroll chords and lyrics automatically with Backing Track Sync and Auto BPM via Microphone.",
        keywords: "teleprompter for musicians, lyrics scroller, chord teleprompter, setlist app, auto scroll lyrics, stage prompter, promptninja music, backing track sync",
      },
      pwa: {
        title: "Install on Your Stage Tablet",
        subtitle: "Offline, Fast, and No Notifications.",
        cta: "Install Stage App",
      },
      faq: {
        title: "Musician FAQ",
        items: [
          {
            q: "How to sync with a pedal?",
            a: "PromptNinja natively recognizes keys like PageUp, PageDown, Home and End which are standard for most bluetooth pedals (even generic ones). Just pair and play.",
          },
          {
            q: "Does it work for 30+ song setlists?",
            a: "Yes! You can create unlimited setlists. Loading is instant since everything runs locally in your browser.",
          },
          {
            q: "How is it different from apps like Cube Turner?",
            a: "PromptNinja is cross-platform (run anywhere) and features unique P2P remote control, allowing a bandmate to control lyrics from their phone if needed.",
          },
          {
            q: "Do I need internet at the show?",
            a: "No. We recommend loading the page once with internet. After that, it works 100% offline (PWA).",
          }
        ]
      },
      cta: {
        title: "Ready for Your Next Show?",
        subtitle: "Over 500 musicians already use PromptNinja on stage.",
        ctaPrimary: "Free Trial at Rehearsal",
        ctaSecondary: "See PRO Plans",
        footer: "Simple. Fast. Made for musicians.",
      },
      pricing: {
        title: "PRO Lifetime for $19",
        subtitle: "One-time payment, yours forever. Focused on stage performance.",
        badge: "MUSICIAN OFFER",
        features: [
          "Unlimited Setlists",
          "Backing Track Sync (Unlimited)",
          "Auto BPM via Microphone",
          "Custom MIDI Control",
          "Offline PWA (Stage App)",
          "Bluetooth Pedal Support",
          "No Logos / Watermarks",
          "Lifetime Access (No Monthly Fees)"
        ],
        price: "$19",
        priceNote: "One-time promotional price for musicians.",
        timer: "⏰ Secure your spot on stage with a discount"
      },
      bpmSync: "BPM Sync",
      bpmTeaser: "Auto-sync with mic (detects real beats) PRO exclusive → $19 lifetime",
      backingTrack: {
        upload: "Add Backing Track",
        remove: "Remove",
        largeFile: "File too large. 50MB limit.",
        error: "Audio upload failed.",
        preview: "Preview (10s)",
        syncActive: "Audio Sync Active",
      },
    },
    footer: {
      backToGeneral: "Back to General Teleprompter",
    },
  },
  midi: {
    title: "Configure MIDI",
    enabled: "Enable MIDI Mode",
    disabled: "Disable MIDI Mode",
    pressPedal: "Press pedal or key...",
    mapping: "MIDI Mapping",
    actions: {
      TOGGLE_PLAY: "Play / Pause",
      NEXT_SONG: "Next Song / Part",
      PREV_SONG: "Previous Song / Part",
      START_SCROLL: "Start Scrolling",
      STOP_SCROLL: "Stop Scrolling",
      SPEED_UP: "Speed Up",
      SPEED_DOWN: "Speed Down",
      FONT_INCREASE: "Increase Font Size",
      FONT_DECREASE: "Decrease Font Size",
      RESET: "Reset",
      TOGGLE_MIRROR: "Mirror",
      EXIT: "Exit",
    },
    proDescription: "Connecting MIDI pedals and OBS Studio is exclusive for PRO members.",
  },

  obs: {
    title: "OBS Studio Integration",
    connect: "Connect",
    disconnect: "Disconnect",
    statusConnected: "Connected",
    statusDisconnected: "Disconnected",
    host: "Host / IP",
    port: "Port",
    password: "Password",
    automation: "Ninja Automation",
    autoRecord: "Record on OBS when Play",
    autoPlay: "Play when Record on OBS",
    errorConnection: "Failed to connect. Check if OBS WebSocket (v5) is enabled.",
  },
  onboarding: {
    title: "Welcome to PromptNinja! 🥷",
    subtitle: "Your journey to flawless videos starts now",
    cta: "Next",
    finish: "Start Now!",
    skip: "Skip Tour",
    steps: [
      {
        title: "Look at the Camera",
        desc: "Read your script while maintaining perfect eye contact. PromptNinja is designed for you to speak naturally without forgetting your lines.",
        icon: "camera"
      },
      {
        title: "Your Phone is the Remote",
        desc: "Use our zero-lag P2P technology. Scan the QR Code with your phone and control everything remotely: speed, play, and pause.",
        icon: "remote"
      },
      {
        title: "Ninja Power (PRO)",
        desc: "Master the stage with Voice Control, custom shortcuts, and unlimited sessions. Transform your browser into a full studio.",
        icon: "pro"
      }
    ]
  },
  master: {
    title: "MASTER MODE",
    subtitle: "Control Multiple Teleprompters",
    exampleTitle: "Example Script",
    exampleContent: "Welcome to PromptNinja Event Mode!\n\nThis is your central hub.",
    connectDisplay: "Connect Display",
    idPlaceholder: "Enter display ID...",
    add: "ADD",
    connectedDisplays: "CONNECTED DISPLAYS",
    noDisplays: "No displays connected. Add an ID above to start.",
    globalControl: "GLOBAL CONTROL",
    playAll: "PLAY ALL",
    pauseAll: "PAUSE ALL",
    resetAll: "RESET ALL",
    scriptLibrary: "SCRIPT LIBRARY",
    noScripts: "No scripts saved in local database.",
    createScriptsHint: "Create scripts to send to your displays remotely.",
    selectedScript: "Selected Script",
    deployHint: "Choose which display(s) you want to send this text to:",
    confirmDelete: "Do you really want to remove this?",
    pushAll: "SEND TO ALL",
    editingLocal: "Editing Local Script",
    scriptTitle: "Script Title",
    scriptContent: "Script content...",
    saveChanges: "SAVE CHANGES",
    limitReached: "Limit reached: Only 1 remote control in the free plan. Upgrade to Pro!",
    displayActions: {
      play: "Play",
      pause: "Pause",
      edit: "Edit",
      remove: "Remove",
      push: "Push",
    }
  }
};

